import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { useCompleteOnboarding } from '@/api/hooks/useOnboarding';
import { usePageTitle } from '@/hooks/usePageTitle';
import { CHART_COLORS } from '@/utils/chartColors';
import type { Color, LedgerType, OnboardingCollectionInput } from '@/api/types';

interface CollectionOption {
    label: string;
    color: Color;
    description: string;
}

const ASSET_COLLECTIONS: CollectionOption[] = [
    { label: 'Investment', color: 'blue', description: 'Stocks, bonds, ETFs, mutual funds' },
    { label: 'Real Estate', color: 'green', description: 'Properties and REITs' },
    { label: 'Fixed', color: 'teal', description: 'Savings, CDs, money market' },
    { label: 'Depreciable', color: 'orange', description: 'Vehicles, equipment, collectibles' },
];

const DEBT_COLLECTIONS: CollectionOption[] = [
    { label: 'Mortgage', color: 'red', description: 'Home loans and property mortgages' },
    { label: 'Student Loan', color: 'indigo', description: 'Education-related debt' },
    { label: 'Credit Card', color: 'pink', description: 'Credit card balances' },
    { label: 'Auto', color: 'gray', description: 'Vehicle loans' },
];

export function OnboardingPage() {
    usePageTitle('Get Started');
    const navigate = useNavigate();
    const { refreshProfile, markOnboardingComplete } = useAuth();
    const completeOnboarding = useCompleteOnboarding();

    const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
    const [selectedDebts, setSelectedDebts] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);

    const hasSelection = selectedAssets.length > 0 || selectedDebts.length > 0;

    const toggleAsset = (label: string) => {
        setSelectedAssets((prev) =>
            prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
        );
    };

    const toggleDebt = (label: string) => {
        setSelectedDebts((prev) =>
            prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
        );
    };

    const handleComplete = async () => {
        if (!hasSelection) {
            setError('Please select at least one collection to continue.');
            return;
        }

        setError(null);

        const collections: OnboardingCollectionInput[] = [
            ...selectedAssets.map((label) => {
                const preset = ASSET_COLLECTIONS.find((c) => c.label === label)!;
                return { label, type: 'asset' as LedgerType, color: preset.color };
            }),
            ...selectedDebts.map((label) => {
                const preset = DEBT_COLLECTIONS.find((c) => c.label === label)!;
                return { label, type: 'debt' as LedgerType, color: preset.color };
            }),
        ];

        try {
            await completeOnboarding.mutateAsync({ collections });
            markOnboardingComplete(selectedAssets.length > 0, selectedDebts.length > 0);
            await refreshProfile();
            navigate('/dashboard');
        } catch {
            setError('Failed to complete setup. Please try again.');
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div>
                    <PageHeader title="Get Started" />
                    <p className="mt-2 text-gray-600 dark:text-gray-400">
                        Select the types of assets and debts you want to track.
                    </p>
                </div>

                {/* Assets and Debts side by side */}
                <div className="grid md:grid-cols-2 gap-6">
                    {/* Assets */}
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary p-6">
                        <div className="flex items-center gap-2 mb-1">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS.asset.fill }} />
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Assets
                            </h3>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            Things you own that have value
                        </p>
                        <div className="space-y-2">
                            {ASSET_COLLECTIONS.map((collection) => (
                                <label
                                    key={collection.label}
                                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                                >
                                    <Checkbox
                                        checked={selectedAssets.includes(collection.label)}
                                        onChange={() => toggleAsset(collection.label)}
                                        className="shrink-0"
                                    />
                                    <span className="text-gray-900 dark:text-white">
                                        <span className="font-medium">{collection.label}</span>
                                        <span className="text-sm text-gray-500 dark:text-gray-400"> — {collection.description}</span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Debts */}
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary p-6">
                        <div className="flex items-center gap-2 mb-1">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS.debt.fill }} />
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Debts
                            </h3>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            Money you owe to others
                        </p>
                        <div className="space-y-2">
                            {DEBT_COLLECTIONS.map((collection) => (
                                <label
                                    key={collection.label}
                                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                                >
                                    <Checkbox
                                        checked={selectedDebts.includes(collection.label)}
                                        onChange={() => toggleDebt(collection.label)}
                                        className="shrink-0"
                                    />
                                    <span className="text-gray-900 dark:text-white">
                                        <span className="font-medium">{collection.label}</span>
                                        <span className="text-sm text-gray-500 dark:text-gray-400"> — {collection.description}</span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Error message */}
                {error && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                        {error}
                    </div>
                )}

                {/* Submit section */}
                <div className="flex flex-col items-end">
                    <Button
                        onClick={handleComplete}
                        isLoading={completeOnboarding.isPending}
                        disabled={!hasSelection}
                        size="lg"
                    >
                        {hasSelection
                            ? `Continue with ${selectedAssets.length + selectedDebts.length} collection${selectedAssets.length + selectedDebts.length > 1 ? 's' : ''}`
                            : 'Select at least one collection'}
                    </Button>

                    <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                        You can customize your collections and add more in{' '}
                        <Link to="/settings/attribution" className="text-accent hover:underline">
                            Settings
                        </Link>{' '}
                        later.
                    </p>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        Have existing data?{' '}
                        <Link to="/data" className="text-accent hover:underline">
                            Import from a backup
                        </Link>
                    </p>
                </div>

                {/* How it works section */}
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        How It Works
                    </h2>
                    <div className="space-y-3">
                        <div className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
                            <p className="text-gray-600 dark:text-gray-400">
                                <span className="font-medium text-gray-900 dark:text-white">Collections</span> group your finances by category (e.g., "Investment", "Mortgage")
                            </p>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
                            <p className="text-gray-600 dark:text-gray-400">
                                Each collection contains individual <span className="font-medium text-gray-900 dark:text-white">assets</span> or <span className="font-medium text-gray-900 dark:text-white">debts</span> you want to track
                            </p>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
                            <p className="text-gray-600 dark:text-gray-400">
                                Record <span className="font-medium text-gray-900 dark:text-white">values</span> over time to see historical trends and growth
                            </p>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
                            <p className="text-gray-600 dark:text-gray-400">
                                Track <span className="font-medium text-gray-900 dark:text-white">cash flow</span> (deposits, withdrawals) to unlock P&L and return rate analytics
                            </p>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
                            <p className="text-gray-600 dark:text-gray-400">
                                <span className="font-medium text-gray-900 dark:text-white">Net Worth</span> = Assets − Debts
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
